/**
 * Graphics settings for the lobby 3D scene.
 *
 * Four presets (Lite/Medium/High/Ultra) gate the most expensive parts of
 * the scene so the lobby stays responsive on weaker hardware. Defaults are
 * auto-detected from the browser / device; users can override via the top-
 * bar menu. The choice is persisted in localStorage.
 *
 * `lite` is a dedicated 2D bird's-eye mode — same seats, no three.js canvas,
 * no tile animations. Sound effects still fire.
 *
 * ## Skybox theming vs these presets
 *
 * All six stable `SkyboxTheme` IDs share the same render path; per-theme
 * art direction and Ultra "signature" motion live in `theming/skybox-
 * briefs.ts`. Rough mapping:
 *
 * - **Lite** — 2D bird's-eye, no WebGL sky.
 * - **Medium** — 3D with HDRI + PMREM + shader overlay; reflections off.
 * - **High** — adds shadows, reflections, higher particle cap, balanced LOD.
 * - **Ultra** — always-full tile overlay LOD; particles at max.
 *
 * ## Auto-detection philosophy (`detectDefaultLevel`)
 *
 * The detector is **opt-in for 3D**: we upgrade to `medium` / `high` /
 * `ultra` only when we see positive evidence a machine can handle it, and
 * default anything unrecognised to `lite`. `navigator.deviceMemory` /
 * `hardwareConcurrency` alone can no longer distinguish a beefy desktop
 * from a 32 GB Strix Halo laptop whose Chrome-reachable GPU is the iGPU —
 * hence the renderer-string gates.
 *
 * Three layers of defence, in order:
 *
 * 1. `probeGpu()` routes known-weak integrated / software GPUs straight to
 *    `lite` (Intel UHD ≤ 770, AMD Ryzen APU iGPUs incl. Strix Halo, Swift-
 *    Shader, llvmpipe, MAX_TEXTURE_SIZE < 8k).
 * 2. `hasDiscreteGpuSignal()` gates `ultra` on an affirmative dGPU / Apple
 *    Silicon / Intel Arc A|B signal plus `cores >= 8`.
 * 3. Everything else — desktop-class but unidentified — falls to `lite`.
 *    User can opt into 3D from the graphics menu or onboarding prompt.
 *
 * All renderer matches go through `rendererMatchesAny` (whitespace-
 * insensitive) because ANGLE has shipped both `Radeon(TM) Graphics` and
 * `Radeon (TM) Graphics` — spaced and unspaced — on the same chip across
 * driver/Chromium combos.
 *
 * ## Crash recovery — the boot sentinel
 *
 * Even perfect static detection can't prevent a user manually picking
 * `ultra` on an iGPU laptop and looping through crashes (manual pick sticks
 * in localStorage; every reload re-enters the wedge).
 *
 * `writeBootSentinel(level)` runs immediately before a 3D canvas mount;
 * `clearBootSentinel()` runs on the first painted frame. A sentinel still
 * present (and within `BOOT_SENTINEL_STALE_MS`) on the next load means the
 * previous session failed to paint at the chosen preset — the recovery
 * path in `GraphicsContext` force-downgrades to `lite` and clears any
 * manual selection so the user isn't re-crashed on reload.
 *
 * ## Escape hatches
 *
 * - `?graphics=lite|medium|high|ultra|auto` — one-shot URL override (see
 *   `consumeGraphicsParam`). Consumed + stripped on first mount.
 * - `clearStoredLevel()` — recovery path wipes manual selection.
 *
 * ## Investigation docs (concrete failure shapes)
 *
 * - `docs/investigations/2026-05-01-windows-i5-11400-crash.md`
 *   — Intel UHD 730 PBR/PMREM stall pattern.
 * - `docs/investigations/2026-05-03-amd-igpu-strix-halo-freeze.md`
 *   — AMD iGPU crash including ANGLE's `(0x000015E7)` device-ID form, the
 *     whitespace-variant renderer strings, and the unlisted-GPU default.
 */

export type GraphicsLevel = 'lite' | 'medium' | 'high' | 'ultra';

export interface GraphicsConfig {
  level: GraphicsLevel;
  label: string;

  /** Render board as a 2D top-down grid instead of the 3D R3F canvas. */
  twoD: boolean;
  /** Tile state/transition animations (buyout crumble, claim burst, etc.). */
  animations: boolean;

  /** Max device pixel ratio passed to the Canvas. */
  dprMax: number;
  /** Enable WebGL antialiasing. */
  antialias: boolean;
  /** Render dynamic shadows (directional light + receive). */
  shadows: boolean;

  /** Reflective ground plane (MeshReflectorMaterial). 0 disables. */
  reflection: boolean;
  /** Reflection render-target resolution. 0 disables. */
  reflectionRes: number;

  /** ContactShadows bake frame count. 0 disables contact shadows entirely. */
  contactShadowsFrames: number;

  /** Particle field count. 0 disables. */
  particles: number;

  /** Build & apply a procedural PMREM env map for reflections. */
  envMap: boolean;

  /** Animate skybox shader time (aurora bands, nebula drift, clouds, etc.). */
  skyboxAnimation: boolean;

  /** Use the stone micro-normal map on tiles. */
  stoneNormals: boolean;

  /** Tiles use MeshStandardMaterial (true) or MeshLambertMaterial fallback (false). */
  pbrTiles: boolean;

  /**
   * Per-tile HTML overlay LOD policy. Controls how aggressively distant
   * tiles drop their avatar / price / chip overlays. The instanced tile
   * mesh itself is always one draw call regardless of policy — only the
   * CSS3D overlays (which dominate the cost at 200+ seats) are gated.
   *
   *   - `aggressive`  — short draw distance: only nearby tiles render
   *                     Full overlays; mid/far tiles drop to Compact /
   *                     Hidden quickly so weaker GPUs stay smooth.
   *   - `balanced`    — pixel-size hysteresis tuned for typical desktop
   *                     framings (the historical default).
   *   - `always-full` — every on-screen tile renders Full overlays
   *                     regardless of distance. Off-screen tiles still
   *                     cull (frustum) so the frame stays responsive
   *                     even on high-density boards.
   */
  lodPolicy: 'aggressive' | 'balanced' | 'always-full';
}

export const GRAPHICS_PRESETS: Record<GraphicsLevel, GraphicsConfig> = {
  lite: {
    level: 'lite',
    label: 'Lite',
    twoD: true,
    animations: false,
    dprMax: 1.0,
    antialias: false,
    shadows: false,
    reflection: false,
    reflectionRes: 0,
    contactShadowsFrames: 0,
    particles: 0,
    envMap: false,
    skyboxAnimation: false,
    stoneNormals: false,
    pbrTiles: false,
    // 2D mode bypasses the 3D LOD computer entirely; value only kept
    // for shape consistency.
    lodPolicy: 'aggressive',
  },
  medium: {
    level: 'medium',
    label: 'Medium',
    twoD: false,
    animations: true,
    dprMax: 1.25,
    antialias: true,
    shadows: false,
    reflection: false,
    reflectionRes: 0,
    contactShadowsFrames: 30,
    particles: 60,
    envMap: true,
    skyboxAnimation: true,
    stoneNormals: true,
    pbrTiles: true,
    lodPolicy: 'aggressive',
  },
  high: {
    level: 'high',
    label: 'High',
    twoD: false,
    animations: true,
    dprMax: 1.75,
    antialias: true,
    shadows: true,
    reflection: true,
    reflectionRes: 256,
    contactShadowsFrames: 60,
    particles: 140,
    envMap: true,
    skyboxAnimation: true,
    stoneNormals: true,
    pbrTiles: true,
    lodPolicy: 'balanced',
  },
  ultra: {
    level: 'ultra',
    label: 'Ultra',
    twoD: false,
    animations: true,
    // Cap DPR — 2× on Retina doubles fragment cost; gains taper off vs CPU
    // for CSS3D tile overlays (hundreds of Html nodes per board).
    dprMax: 1.75,
    antialias: true,
    shadows: true,
    reflection: true,
    // 512 RT reflections + large boards melted even on M-series; 256 is
    // visually similar with far less GPU fill.
    reflectionRes: 256,
    contactShadowsFrames: 60,
    particles: 160,
    envMap: true,
    skyboxAnimation: true,
    stoneNormals: true,
    pbrTiles: true,
    // Every on-screen tile stays at Full LOD: full avatars + price chips + no
    // distance downgrade for Html. Off-screen tiles still cull. Large boards
    // lean on the 2D cliff above `LARGE_BOARD_FORCE_2D_THRESHOLD` if needed.
    lodPolicy: 'always-full',
  },
};

export const GRAPHICS_LEVELS: GraphicsLevel[] = ['lite', 'medium', 'high', 'ultra'];

/**
 * Auto-fallback threshold: any board with strictly more than this many seats
 * is forced into 2D regardless of the user's chosen graphics level.
 *
 * Why: each held tile renders a stack of `<Html transform>` overlays
 * (avatar, price, vacant chip, forfeit, selected, effects). At ~256 tiles
 * the DOM is roughly 1k–2k absolutely-positioned `CSS3DObject`s and the
 * browser still keeps up; at 1024 tiles it is ~7k overlays and the main
 * thread starts dropping frames during pan/zoom. Snapping to the 2D bird's-
 * eye view sidesteps the CSS3D bottleneck entirely.
 *
 * Set above the current 16×16 contract cap (256) and the next sensible
 * step (20×20 = 400), so today's boards are unaffected. The cliff fires at
 * 24×24 (576) and beyond — that's where overlay churn becomes the
 * dominant cost regardless of GPU tier.
 *
 * The override is an automatic safety net, not a hard ceiling — users can
 * still pick Lite manually for smaller boards. The 3D presets remain
 * fully selectable below the threshold.
 */
export const LARGE_BOARD_FORCE_2D_THRESHOLD = 512;

/**
 * Returns the effective 2D switch given the user's preset and the current
 * board size. Wrapped as a tiny helper so the rule is centralised — any
 * future cliff (e.g. drop env maps at 256+) can live next to it.
 */
export function shouldForceTwoD(seatCount: number, configTwoD: boolean): boolean {
  if (configTwoD) return true;
  return seatCount > LARGE_BOARD_FORCE_2D_THRESHOLD;
}

export const STORAGE_KEY = 'takeover-lobby-graphics-level';
export const STORAGE_AUTO_KEY = 'takeover-lobby-graphics-auto';

/** Boot sentinel key — see module header "Crash recovery" section. */
export const STORAGE_BOOT_SENTINEL_KEY =
  'takeover-lobby-graphics-boot-sentinel';

/** Sentinel staleness window. Beyond this, a stored marker is treated as
 *  a clean tab close from long ago rather than a crash to recover from.
 *  5 min covers "laptop closed mid-load, reopened after lunch" without
 *  accidentally dropping next-day returners into 2D. */
export const BOOT_SENTINEL_STALE_MS = 5 * 60 * 1000;

export interface BootSentinel {
  level: GraphicsLevel;
  ts: number;
  /** `'manual'` = user set via graphics menu / URL param;
   *  `'auto'` = detector output. Recovery clears the persisted
   *  manual selection but leaves auto alone. */
  source: 'auto' | 'manual';
}

export interface DetectedLevel {
  level: GraphicsLevel;
  reason: string;
}

interface GpuProbe {
  /** True when WebGL is unavailable, the browser is using a software
   *  rasteriser (SwiftShader / llvmpipe), or the GPU is on the known
   *  list of integrated chips that consistently struggle with the
   *  scene's PBR + shadow + PMREM stack. */
  weak: boolean;
  /** Lower-cased renderer string from `WEBGL_debug_renderer_info`,
   *  or one of the synthetic markers `'no-webgl'` / `'gl-error'`.
   *  `null` when the probe couldn't run (SSR / no `document`). */
  renderer: string | null;
}

/**
 * Whitespace-insensitive substring matcher for renderer strings — ANGLE
 * emits both `Radeon(TM) Graphics` and `Radeon (TM) Graphics` for the
 * same chip across driver/Chromium combos. See module header.
 */
function rendererMatchesAny(
  rendererLower: string,
  needles: readonly string[],
): boolean {
  const squashed = rendererLower.replace(/\s+/g, '');
  for (const needle of needles) {
    const squashedNeedle = needle.replace(/\s+/g, '');
    if (squashedNeedle.length > 0 && squashed.includes(squashedNeedle)) {
      return true;
    }
  }
  return false;
}

/**
 * Renderer-string markers that positively identify a *discrete* GPU (or
 * Apple Silicon). Used by `detectDefaultLevel` to gate `ultra` on evidence
 * a capable GPU is actually reachable — a Ryzen 9 laptop whose Chrome
 * tab is stuck on the AMD iGPU otherwise reads as "32 GB / 16 cores" and
 * routes into ultra, then hangs. See module header + the Strix Halo
 * investigation doc.
 *
 * Intentional conservatism:
 *   - `radeon(tm) rx` / `radeon(tm) pro` — discrete only. The bare
 *     `radeon(tm) graphics` / `radeon(tm) 780m` iGPU forms are NOT here.
 *   - `nvidia` / `geforce` / `quadro` — covers every NVIDIA renderer.
 *   - `apple m` — M1+, runs ultra cleanly.
 *   - `arc(tm) a|b` / `arc a|b<n>` — Intel discrete Arc A-series (Alchemist)
 *     and B-series (Battlemage). The integrated `arc(tm) graphics` (Meteor
 *     Lake / Lunar Lake) form intentionally doesn't match — it falls
 *     through to the `high`-capable branch instead of ultra.
 */
const DISCRETE_GPU_MARKERS: readonly string[] = [
  'nvidia',
  'geforce',
  'quadro',
  'radeon(tm) rx',
  'radeon rx',
  'radeon(tm) pro',
  'radeon pro',
  'apple m',
  'arc(tm) a',
  'arc(tm) b',
  'arc a7',
  'arc a5',
  'arc a3',
  'arc b5',
  'arc b3',
];

function hasDiscreteGpuSignal(rendererLower: string | null): boolean {
  if (!rendererLower) return false;
  return rendererMatchesAny(rendererLower, DISCRETE_GPU_MARKERS);
}

/**
 * Drops the memoised probe so the next `detectDefaultLevel()` / `probeGpu()`
 * spins a fresh throwaway GL context — used when user hits "Graphics: Auto"
 * in settings or after a deferred first-visit idle probe concludes.
 */
export function invalidateGpuProbeCache(): void {
  memoisedGpuProbe = null;
}

let memoisedGpuProbe: GpuProbe | null | undefined = undefined;

function probeGpuInternal(): GpuProbe {
  if (typeof document === 'undefined') return { weak: false, renderer: null };
  let canvas: HTMLCanvasElement | null = null;
  try {
    canvas = document.createElement('canvas');
    const gl =
      (canvas.getContext('webgl2') as WebGL2RenderingContext | null) ||
      (canvas.getContext('webgl') as WebGLRenderingContext | null);
    if (!gl) return { weak: true, renderer: 'no-webgl' };

    const dbg = gl.getExtension('WEBGL_debug_renderer_info');
    const raw = dbg
      ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL) ?? '')
      : '';
    const renderer = raw.toLowerCase();

    // Software rasterisers — <10fps on our scene, frequent GPU-process
    // stalls in Chrome.
    if (
      rendererMatchesAny(renderer, [
        'swiftshader',
        'llvmpipe',
        'software',
        'basic render' /* Direct3D9 fallback */,
      ])
    ) {
      return { weak: true, renderer };
    }

    // Older Intel integrated (HD ≤ 5500, UHD 6xx) + entry-tier Xe-LP
    // desktop UHD 7xx (Rocket/Alder/Raptor Lake i3/i5 prebuilts):
    // shader compile stalls the GPU process for seconds on first PBR +
    // shadow material. Newer Iris Xe is fine — match only "HD" / "UHD"
    // prefixes, not "Iris". Two forms per family: ANGLE (`intel(r) uhd
    // graphics 7…`) and Mesa (`uhd graphics 7xx`).
    // See docs/investigations/2026-05-01-windows-i5-11400-crash.md.
    if (
      rendererMatchesAny(renderer, [
        'intel(r) hd graphics',
        'intel(r) uhd graphics 6',
        'intel(r) uhd graphics 7',
        'uhd graphics 710',
        'uhd graphics 730',
        'uhd graphics 750',
        'uhd graphics 770',
      ])
    ) {
      return { weak: true, renderer };
    }

    // AMD Ryzen APU iGPUs — Vega + RDNA2/3/3.5 mobile + the 2025 Strix
    // Halo / Strix Point class. Same silicon powers Steam Deck / ROG
    // Ally, but also shows up as the only Chrome-reachable GPU on many
    // Ryzen HS / U laptops (Windows defaults browser tabs to the iGPU
    // unless the user flips "High performance" in Graphics Settings).
    // Consistently stalls PBR + shadow + PMREM on first paint or board
    // swap. See the 2026-05-01 (UHD 730) + 2026-05-03 (Strix Halo) docs.
    //
    // `(0x0000…)` clause catches just-released chips where ANGLE hasn't
    // cached a marketing name and emits the raw PCI device ID (the
    // exact form for Strix Halo: `"AMD Radeon (TM) Graphics (0x000015E7)"`).
    // A `radeon` + numeric-id combo is never a discrete in the wild —
    // those resolve to `RX xxxx` / `Pro Wxxxx` first.
    if (
      rendererMatchesAny(renderer, [
        // Vega-era (Ryzen 2000–5000 APUs): bare "Radeon(TM) Graphics".
        'radeon(tm) graphics',
        'radeon(tm) vega',
        'radeon vega',
        // Linux/Mesa generic AMD iGPU + Steam Deck Aerith/Sephiroth.
        'amd radeon graphics',
        'amd custom gpu',
        // RDNA2 (Rembrandt, 6000U/H), RDNA3 (Phoenix/Hawk Point),
        // RDNA3.5 (Strix Point / Ryzen AI 300). Mobile suffixes only —
        // anchored on `(tm) N…m` so desktop RX 6800M etc don't match.
        '(tm) 660m',
        '(tm) 680m',
        '(tm) 740m',
        '(tm) 760m',
        '(tm) 780m',
        '(tm) 860m',
        '(tm) 880m',
        '(tm) 890m',
      ]) ||
      (renderer.includes('radeon') && renderer.includes('(0x0000'))
    ) {
      return { weak: true, renderer };
    }

    // MAX_TEXTURE_SIZE < 8k: either a lying software renderer or a
    // phone-class mobile GPU on a desktop UA. Either way → lite.
    const maxTex = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number | undefined;
    if (typeof maxTex === 'number' && maxTex > 0 && maxTex < 8192) {
      return { weak: true, renderer: renderer || `low-max-tex-${maxTex}` };
    }

    return { weak: false, renderer: renderer || null };
  } catch {
    return { weak: true, renderer: 'gl-error' };
  } finally {
    // Drop context explicitly — Chrome's per-tab WebGL pool is hard-
    // capped and the real Canvas needs that slot a tick later.
    if (canvas) {
      try {
        const lose = (
          canvas.getContext('webgl2') ||
          canvas.getContext('webgl')
        )?.getExtension('WEBGL_lose_context');
        lose?.loseContext();
      } catch {
        // best-effort
      }
    }
  }
}

/**
 * Tiny throwaway WebGL context — result memoised once per navigation so
 * unrelated calls (GraphicsProvider resolve + diagnostics) don't churn
 * extra GL contexts in fragile Windows/ANGLE stacks.
 */
function probeGpu(): GpuProbe {
  if (memoisedGpuProbe !== undefined && memoisedGpuProbe !== null) {
    return memoisedGpuProbe;
  }
  const next = probeGpuInternal();
  memoisedGpuProbe = next;
  return next;
}

/**
 * True when `?graphics=` is present (any value). Used to skip deferring the
 * first-visit idle GPU probe — the URL is an explicit rescue / override hook.
 */
export function peekGraphicsUrlHasParam(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const url = new URL(window.location.href);
    return url.searchParams.has('graphics');
  } catch {
    return false;
  }
}

/**
 * `(hover: none) and (pointer: coarse)` — touch-only, no precise pointer.
 * Cleanly separates finger/thumbstick-driven devices (handhelds, phones,
 * tablets without trackpads) from anything with mouse/trackpad/stylus.
 * A 2-in-1 with the keyboard detached returns coarse; docked returns fine,
 * so the same chassis adapts live.
 */
function isTouchPrimaryDevice(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return false;
  }
  try {
    return window.matchMedia('(hover: none) and (pointer: coarse)').matches;
  } catch {
    return false;
  }
}

/**
 * Heuristic — handheld gaming PCs (ROG Ally, Legion Go, MSI Claw, AYANEO,
 * OneXPlayer, Steam Deck) run Ryzen Z / 7x40U-class APUs at 8–15 W TDP,
 * ship 16 GB / 8c16t, and report touch-primary input. Without this guard
 * they read as desktops and get routed into `ultra`, which thrashes their
 * throttled GPU into the "page never paints" failure. False positives
 * (AMD-APU ultraportables in detached-keyboard mode) correctly land on
 * `lite` too.
 */
function looksLikeHandheldPC(rendererLower: string | null): boolean {
  if (!rendererLower) return false;
  if (!isTouchPrimaryDevice()) return false;
  return (
    rendererLower.includes('amd radeon(tm)') ||
    rendererLower.includes('amd radeon graphics') ||
    rendererLower.includes('amd custom gpu') ||
    rendererLower.includes('radeon(tm) graphics') ||
    rendererLower.includes('radeon(tm) 7') ||
    rendererLower.includes('radeon(tm) 8') ||
    rendererLower.includes('radeon(tm) 6')
  );
}

/**
 * Best-effort auto-detection based on navigator hints + a quick WebGL
 * renderer probe. Falls back to 'lite' for software rasterisers and
 * known-weak integrated GPUs, 'high' on modern desktops, 'medium' for
 * tablets / low-memory machines, 'lite' on mobile. Never throws.
 */
export function detectDefaultLevel(): DetectedLevel {
  if (typeof navigator === 'undefined') {
    return { level: 'high', reason: 'SSR default' };
  }

  const ua = navigator.userAgent || '';
  const isMobilePhone = /iPhone|Android.*Mobile|Mobile.*Firefox|IEMobile|Opera Mini/i.test(ua);
  const isTablet = /iPad|Android(?!.*Mobile)|Tablet/i.test(ua);

  if (isMobilePhone) {
    return { level: 'lite', reason: 'Mobile device' };
  }

  // GPU probe before navigator-hint heuristics: a beefy-looking machine
  // on SwiftShader (headless Chrome, RDP, broken driver) should still
  // route to 2D, not crash the GPU process. Probe is ~1ms sync.
  const gpu = probeGpu();
  if (gpu.weak) {
    return {
      level: 'lite',
      reason: gpu.renderer
        ? `Weak GPU (${gpu.renderer})`
        : 'Weak GPU detected',
    };
  }

  // Handhelds before the upgrade branch — they report like real desktops
  // through navigator hints.
  if (looksLikeHandheldPC(gpu.renderer)) {
    return {
      level: 'lite',
      reason: `Handheld PC (${gpu.renderer})`,
    };
  }

  const mem = (navigator as Navigator & { deviceMemory?: number }).deviceMemory;
  const cores = navigator.hardwareConcurrency ?? 4;

  if (isTablet) {
    return { level: 'medium', reason: 'Tablet device' };
  }

  if (mem !== undefined && mem <= 4) {
    return { level: 'medium', reason: `Limited memory (${mem}GB)` };
  }

  if (cores <= 4) {
    return { level: 'medium', reason: `Low core count (${cores})` };
  }

  // Auto-`ultra` requires an affirmative discrete-GPU (or Apple Silicon)
  // signal plus `cores >= 8`. Navigator hints alone can't distinguish a
  // genuine desktop from an iGPU laptop with 32 GB shared RAM and a
  // high core count — see module header "Auto-detection philosophy".
  if (hasDiscreteGpuSignal(gpu.renderer) && cores >= 8) {
    return {
      level: 'ultra',
      reason: `Discrete GPU · ${cores} cores${
        gpu.renderer ? ` · ${gpu.renderer}` : ''
      }`,
    };
  }

  // Capable-but-unlisted → 2D. See module header "Auto-detection
  // philosophy": historical policy was "unknown → high"; we now default
  // to `lite` and let the user opt into 3D from the graphics menu or
  // onboarding prompt. Eliminates the long-tail freeze class at the
  // cost of one extra click for users on unidentified capable hardware.
  return {
    level: 'lite',
    reason: gpu.renderer
      ? `Unlisted GPU (${gpu.renderer}) — 2D default. Enable 3D from graphics settings.`
      : 'Unlisted GPU — 2D default. Enable 3D from graphics settings.',
  };
}

export function loadStoredLevel(): GraphicsLevel | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'low') return 'lite';
    if (raw && (GRAPHICS_LEVELS as string[]).includes(raw)) {
      return raw as GraphicsLevel;
    }
  } catch {
    // ignore localStorage errors
  }
  return null;
}

/**
 * URL override — `?graphics=lite|medium|high|ultra|auto`. Consumed + stripped
 * on first mount so a shared "?graphics=lite" link flips the recipient to
 * 2D without trapping them after subsequent in-app changes. `auto` clears
 * the manual override to re-arm detection. Safe during SSR.
 */
export function consumeGraphicsParam(): GraphicsLevel | 'auto' | null {
  if (typeof window === 'undefined') return null;
  let url: URL;
  try {
    url = new URL(window.location.href);
  } catch {
    return null;
  }
  const raw = url.searchParams.get('graphics');
  if (raw == null) return null;

  const normalised = raw.trim().toLowerCase();
  // Strip the param so the next refresh / share doesn't re-apply it.
  url.searchParams.delete('graphics');
  try {
    window.history.replaceState({}, '', url.toString());
  } catch {
    // ignore — privacy-mode etc.
  }

  if (normalised === 'auto') return 'auto';
  if (normalised === 'low') return 'lite';
  if ((GRAPHICS_LEVELS as string[]).includes(normalised)) {
    return normalised as GraphicsLevel;
  }
  return null;
}

export function storeLevel(level: GraphicsLevel, auto: boolean) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(STORAGE_KEY, level);
    window.localStorage.setItem(STORAGE_AUTO_KEY, auto ? '1' : '0');
  } catch {
    // ignore
  }
}

export function isAuto(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(STORAGE_AUTO_KEY);
    if (raw == null) return true;
    return raw === '1';
  } catch {
    return true;
  }
}

/** Clear persisted manual selection → next mount re-runs auto-detect.
 *  Used by the boot-sentinel recovery path so a manual `ultra` pick
 *  that crashed doesn't simply re-crash on reload. */
export function clearStoredLevel(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
    window.localStorage.removeItem(STORAGE_AUTO_KEY);
  } catch {
    // ignore — private-mode Safari, cookies blocked, etc.
  }
}

/**
 * Write before a 3D canvas starts compiling shaders. Pair with
 * `clearBootSentinel` on first painted frame. No-op for `lite`
 * (2D doesn't touch WebGL; a sentinel there would only produce
 * spurious recovery banners on clean tab closes). Never throws.
 */
export function writeBootSentinel(level: GraphicsLevel): void {
  if (typeof window === 'undefined') return;
  if (level === 'lite') return;
  try {
    const payload: BootSentinel = {
      level,
      ts: Date.now(),
      source: isAuto() ? 'auto' : 'manual',
    };
    window.localStorage.setItem(
      STORAGE_BOOT_SENTINEL_KEY,
      JSON.stringify(payload),
    );
  } catch {
    // ignore
  }
}

export function clearBootSentinel(): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.removeItem(STORAGE_BOOT_SENTINEL_KEY);
  } catch {
    // ignore
  }
}

/**
 * Read + clear in one call. Returns a valid `BootSentinel` when the
 * previous session wrote but never cleared one and it's within the
 * staleness window; `null` otherwise (stale, malformed, or absent).
 * Always clears — a stale/malformed marker shouldn't keep forcing 2D.
 */
export function consumeBootSentinel(): BootSentinel | null {
  if (typeof window === 'undefined') return null;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(STORAGE_BOOT_SENTINEL_KEY);
    window.localStorage.removeItem(STORAGE_BOOT_SENTINEL_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const candidate = parsed as Partial<BootSentinel>;
  if (
    typeof candidate.ts !== 'number' ||
    typeof candidate.level !== 'string' ||
    !(GRAPHICS_LEVELS as string[]).includes(candidate.level) ||
    (candidate.source !== 'auto' && candidate.source !== 'manual')
  ) {
    return null;
  }
  if (Date.now() - candidate.ts > BOOT_SENTINEL_STALE_MS) {
    return null;
  }
  return {
    level: candidate.level as GraphicsLevel,
    source: candidate.source,
    ts: candidate.ts,
  };
}
